import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import {
    authApi,
    setUnauthorizedHandler,
    type Account,
    ApiCallError,
} from "../api/client";

type Status = "loading" | "anon" | "authed";
type Mode = "mock" | "docore";

interface AuthState {
    status: Status;
    mode: Mode;
    accountId: string | null;
    roles: string[];
    accounts: Account[];
}

interface AuthContextValue extends AuthState {
    login: (email: string, password: string, accountId?: string) => Promise<void>;
    selectAccount: (accountId: string) => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
    return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        status: "loading",
        mode: "mock",
        accountId: null,
        roles: [],
        accounts: [],
    });
    // Guard against a late bootstrap overwriting a fresh login/logout.
    const generation = useRef(0);

    const loadSession = useCallback(async (mode: Mode) => {
        const gen = ++generation.current;
        try {
            const me = await authApi.me();
            let accounts: Account[] = [];
            if (mode === "docore") {
                accounts = await authApi
                    .accounts()
                    .then((r) => r.accounts)
                    .catch(() => []);
            }
            if (gen !== generation.current) return;
            setState({
                status: "authed",
                mode,
                accountId: me.account_id,
                roles: me.roles,
                accounts,
            });
        } catch (e) {
            if (gen !== generation.current) return;
            // 401 = not signed in; anything else we also treat as anon so the
            // login screen is reachable rather than a hard error page.
            void (e instanceof ApiCallError);
            setState((s) => ({ ...s, status: "anon", mode, accountId: null, roles: [] }));
        }
    }, []);

    // Bootstrap: discover the mode, then probe the session.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            const mode = await authApi
                .mode()
                .then((r) => r.mode)
                .catch(() => "mock" as Mode);
            if (cancelled) return;
            await loadSession(mode);
        })();
        return () => {
            cancelled = true;
        };
    }, [loadSession]);

    // Any 401 mid-session drops us back to the login screen (docore mode).
    useEffect(() => {
        setUnauthorizedHandler(() => {
            setState((s) =>
                s.mode === "docore" && s.status === "authed"
                    ? { ...s, status: "anon", accountId: null, roles: [] }
                    : s,
            );
        });
        return () => setUnauthorizedHandler(null);
    }, []);

    const login = useCallback(
        async (email: string, password: string, accountId?: string) => {
            const res = await authApi.login(email, password, accountId);
            generation.current++;
            setState({
                status: "authed",
                mode: "docore",
                accountId: res.account_id,
                roles: [],
                accounts: res.accounts,
            });
            // Refresh roles/capabilities from /me (non-fatal).
            await loadSession("docore");
        },
        [loadSession],
    );

    const selectAccount = useCallback(async (accountId: string) => {
        await authApi.selectAccount(accountId);
        setState((s) => ({ ...s, accountId }));
    }, []);

    const logout = useCallback(async () => {
        await authApi.logout().catch(() => undefined);
        generation.current++;
        setState((s) => ({ ...s, status: "anon", accountId: null, roles: [], accounts: [] }));
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({ ...state, login, selectAccount, logout }),
        [state, login, selectAccount, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
